import { useCallback, useEffect, useId, useState } from 'react';
import type { AiModelConfig, AiProviderKind } from '@cornerstone/shared';
import { api } from '../client/api';
import { useWriteGate } from '../nav/useWriteGate';
import { Button } from './ui';
import { toast } from './toast';

// =============================================================================
// 고객사별 AI 모델 설정 카드 — 고객사 상세(BO 범위)에서만 보인다 (2026-09-22 대교:
// "키를 각 고객사 별로 달리 적용하고 싶어. 고객사 관리 메뉴에 있는게 적합").
//
// 두 모드:
//   · 운영사 기본값 사용 — 운영사 설정의 'AI 모델 기본값'을 그대로 상속(기본). 값은 읽기 전용으로 표시.
//   · 이 고객사 전용 설정 — provider/모델/게이트웨이 주소/키를 따로 저장. 키는 서버에 암호화, 다시 표시 안 됨.
// 고객사 관리자에게는 노출하지 않는다(운영사만). 필드는 운영사 AiSettings 와 같다.
// =============================================================================

const GATEWAY_DEFAULT_BASE_URL = 'https://llm-gateway.ix-works.xyz/v1';
const PROVIDERS: ReadonlyArray<{ value: AiProviderKind; label: string; hint: string }> = [
  { value: 'openai', label: 'LLM Gateway (사내)', hint: 'OpenAI 호환 사내 게이트웨이. Base URL·API 키 입력 후 "모델 불러오기"로 선택.' },
  { value: 'ollama', label: 'Ollama (로컬/자체호스팅)', hint: 'Base URL + 모델 ID. API 키 불필요.' },
];
const SOURCE_LABEL: Record<NonNullable<AiModelConfig['source']> | 'env', string> = {
  org: '이 고객사 전용 설정을 쓰고 있습니다.',
  platform: '운영사 기본값을 상속하고 있습니다. 전용 설정을 저장하면 이 고객사만 바뀝니다.',
  env: '운영사 기본값도 없어 서버 .env 설정(Vertex Gemini)으로 동작합니다.',
};
const inputCls = 'min-w-0 w-full px-3 py-2 border border-line rounded bg-panel-2 text-[14px] text-text disabled:opacity-60';

export function OrgAiConfigCard({ orgId }: { orgId: string }): JSX.Element {
  const id = useId(); const write = useWriteGate();
  const [loaded, setLoaded] = useState<AiModelConfig | null | undefined>(undefined); // undefined = 로딩 전
  const [mode, setMode] = useState<'inherit' | 'own'>('inherit');
  const [provider, setProvider] = useState<AiProviderKind>('openai');
  const [modelId, setModelId] = useState('');
  const [baseUrl, setBaseUrl] = useState(GATEWAY_DEFAULT_BASE_URL);
  const [timeoutMs, setTimeoutMs] = useState(15000);
  const [apiKey, setApiKey] = useState('');
  const [hasApiKey, setHasApiKey] = useState(false);
  const [models, setModels] = useState<string[]>([]);
  const [loading, setLoading] = useState(true); const [saving, setSaving] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = useCallback((cfg: AiModelConfig | null) => {
    setLoaded(cfg);
    const own = cfg?.source === 'org';
    setMode(own ? 'own' : 'inherit');
    if (cfg) {
      setProvider(cfg.provider === 'gemini' ? 'openai' : cfg.provider);
      setModelId(cfg.modelId);
      setBaseUrl(cfg.baseUrl || (cfg.provider === 'openai' ? GATEWAY_DEFAULT_BASE_URL : ''));
      setTimeoutMs(cfg.timeoutMs);
      // 상속 중이면 키는 운영사 것 — 이 고객사 전용 키는 아직 없다.
      setHasApiKey(own && cfg.hasApiKey);
    } else {
      setProvider('openai'); setModelId(''); setBaseUrl(GATEWAY_DEFAULT_BASE_URL); setTimeoutMs(15000); setHasApiKey(false);
    }
    setApiKey(''); setModels([]);
  }, []);

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true); setError(null);
    try {
      const cfg = await api.boGetTenantAiConfig(orgId);
      if (signal?.aborted) return;
      apply(cfg);
    } catch (e) { if (!signal?.aborted) setError(e instanceof Error ? e.message : 'AI 설정을 불러오지 못했습니다.'); }
    finally { if (!signal?.aborted) setLoading(false); }
  }, [orgId, apply]);
  useEffect(() => { const controller = new AbortController(); void load(controller.signal); return () => controller.abort(); }, [load]);

  const editable = !write.blocked && !loading;
  const own = mode === 'own';
  const needsKey = own && provider === 'openai' && !hasApiKey && !apiKey.trim();
  const sourceKey: keyof typeof SOURCE_LABEL = loaded?.source ?? 'env';

  const save = async () => {
    if (!editable || saving || needsKey) return;
    setSaving(true); setError(null);
    try {
      const saved = await api.boSetTenantAiConfig(orgId, {
        provider, modelId: modelId.trim(), baseUrl: baseUrl.trim(), timeoutMs,
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
      });
      apply(saved);
      toast('이 고객사 전용 AI 설정을 저장했습니다. 다음 요청부터 적용됩니다.', 'success');
    } catch (e) { setError(e instanceof Error ? e.message : '저장하지 못했습니다.'); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!editable || saving) return;
    setSaving(true); setError(null);
    try {
      apply(await api.boResetTenantAiConfig(orgId));
      toast('전용 설정을 지웠습니다. 운영사 기본값을 상속합니다.', 'success');
    } catch (e) { setError(e instanceof Error ? e.message : '되돌리지 못했습니다.'); }
    finally { setSaving(false); }
  };
  const loadModels = async () => {
    if (!apiKey.trim() && !hasApiKey) { setError('게이트웨이 API 키를 먼저 입력한 뒤 "모델 불러오기"를 누르세요.'); return; }
    setLoadingModels(true); setError(null);
    try {
      const body: { baseUrl?: string; apiKey?: string } = {};
      if (baseUrl.trim()) body.baseUrl = baseUrl.trim();
      if (apiKey.trim()) body.apiKey = apiKey.trim();
      const { models: ids } = await api.boFetchTenantAiModels(orgId, body);
      setModels(ids);
      if (ids.length === 0) setError('모델 목록이 비어 있습니다.');
    } catch (e) {
      const m = e instanceof Error ? e.message : String(e);
      setError(/401|missing api key|unauthor/i.test(m) ? '게이트웨이 API 키가 유효하지 않습니다. 키를 확인하고 다시 시도하세요.'
        : `모델 목록을 불러오지 못했습니다: ${m.slice(0, 160)}`);
    } finally { setLoadingModels(false); }
  };

  const meta = PROVIDERS.find((p) => p.value === provider) ?? PROVIDERS[0]!;

  return <section className="border border-line rounded-md bg-panel mb-6" aria-labelledby={`${id}-title`}>
    <div className="px-5 py-3 border-b border-line flex flex-wrap items-center justify-between gap-3">
      <h2 id={`${id}-title`} className="text-[15px] font-semibold">AI 모델 설정 (공지 파싱 · 초안 생성)</h2>
      <Button disabled={loading || saving} onClick={() => void load()}>다시 불러오기</Button>
    </div>
    <div className="px-5 py-4 space-y-4">
      {error && <p role="alert" className="text-[13px] text-red">{error}</p>}
      {loading && loaded === undefined && <p role="status" className="text-[13px] text-text-dim">AI 설정을 불러오는 중입니다.</p>}
      {loaded !== undefined && <>
        <p role="status" className="rounded border border-line bg-panel-2 p-3 text-[13px] text-text-dim">{SOURCE_LABEL[sourceKey]}</p>

        <div role="radiogroup" aria-label="AI 설정 방식" className="flex flex-wrap gap-2">
          {([['inherit', '운영사 기본값 사용'], ['own', '이 고객사 전용 설정']] as const).map(([value, label]) => (
            <Button key={value} aria-pressed={mode === value} disabled={!editable || saving} title={write.reason}
              style={mode === value ? { borderColor: 'var(--color-accent)', color: 'var(--color-accent)' } : undefined}
              onClick={() => setMode(value)}>{label}</Button>
          ))}
        </div>

        <div className="flex flex-col gap-4 max-w-[560px]" aria-disabled={!own}>
          <label className="flex flex-col gap-1">
            <span className="text-[13px] text-text-dim">Provider</span>
            <select className={inputCls} value={provider} disabled={!own || !editable || saving}
              onChange={(e) => { const v = e.target.value as AiProviderKind; setProvider(v); setModels([]); if (v === 'openai' && !baseUrl.trim()) setBaseUrl(GATEWAY_DEFAULT_BASE_URL); }}>
              {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <span className="text-[12px] text-text-faint">{meta.hint}</span>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[13px] text-text-dim">Base URL</span>
            <input className={inputCls} value={baseUrl} disabled={!own || !editable || saving}
              placeholder={provider === 'ollama' ? 'http://127.0.0.1:11434' : GATEWAY_DEFAULT_BASE_URL} onChange={(e) => setBaseUrl(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[13px] text-text-dim">모델 ID</span>
            {provider === 'openai' && models.length > 0
              ? <select className={inputCls} value={modelId} disabled={!own || !editable || saving} onChange={(e) => setModelId(e.target.value)}>
                  <option value="">선택</option>{models.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              : <input className={inputCls} value={modelId} disabled={!own || !editable || saving}
                  placeholder={provider === 'ollama' ? 'qwen3:4b' : 'openrouter/google/gemini-2.5-flash'} onChange={(e) => setModelId(e.target.value)} />}
            {provider === 'openai' && own && (
              <Button disabled={!editable || loadingModels || saving} onClick={() => void loadModels()}>
                {loadingModels ? '불러오는 중…' : models.length > 0 ? `모델 다시 불러오기 (${models.length})` : '모델 불러오기'}
              </Button>
            )}
          </label>
          {provider === 'openai' && (
            <label className="flex flex-col gap-1">
              <span className="text-[13px] text-text-dim">API Key {own && hasApiKey && <span className="text-green">· 이 고객사 키 저장됨</span>}</span>
              <input className={inputCls} type="password" value={apiKey} disabled={!own || !editable || saving} autoComplete="new-password"
                placeholder={!own ? '운영사 키를 사용' : hasApiKey ? '변경하려면 새 키 입력 (비우면 유지)' : 'sk-...'} onChange={(e) => setApiKey(e.target.value)} />
              {needsKey && <span className="text-[12px] text-red">전용 설정으로 저장하려면 이 고객사의 API 키가 필요합니다.</span>}
            </label>
          )}
          <label className="flex flex-col gap-1">
            <span className="text-[13px] text-text-dim">타임아웃 (ms)</span>
            <input className={inputCls} type="number" min={1000} step={1000} value={timeoutMs} disabled={!own || !editable || saving}
              onChange={(e) => { const n = Number(e.target.value); if (Number.isFinite(n) && n > 0) setTimeoutMs(Math.round(n)); }} />
          </label>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button variant="primary" disabled={!editable || saving || !own || needsKey} title={write.reason ?? (needsKey ? 'API 키를 입력하세요.' : undefined)}
              onClick={() => void save()}>{saving ? '저장 중…' : '전용 설정 저장'}</Button>
            <Button disabled={!editable || saving || loaded?.source !== 'org'} title={write.reason ?? (loaded?.source !== 'org' ? '지울 전용 설정이 없습니다.' : undefined)}
              onClick={() => void reset()}>전용 설정 지우고 기본값 상속</Button>
          </div>
          {!own && <p className="text-[12px] text-text-dim">‘이 고객사 전용 설정’을 고르면 위 칸을 편집할 수 있습니다. 운영사 기본값은 운영사 설정 &gt; AI 모델 기본값에서 바꿉니다.</p>}
        </div>
      </>}
    </div>
  </section>;
}
